/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Add, Cancel, Close, Save } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Checkbox,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import Button from "@jumbo/components/JumboLoadingButton";
import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import moment from "moment";
import { SALES_INVOICE } from "app/routes/constant";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import Loader from "@jumbo/components/Loader";
import { getProductList } from "app/redux/slice/productSlice";
import { getVariantList } from "app/redux/slice/variantSlice";
import { getPlatformList } from "app/redux/slice/platformSlice";
import {
  createSalesInvoice,
  getByIdSalesInvoice,
  updateSalesInvoice,
} from "app/redux/slice/salesInvoiceSlice";
import { getVendorList } from "app/redux/slice/vendorSlice";

const filter = createFilterOptions();

const useStyles = makeStyles({
  headingTitle: {
    minWidth: "80px",
  },
  table: {
    minWidth: 650,
    boxShadow: "none",
    "& .MuiTableCell-root": {
      boxShadow: "none",
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "6px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
  imageUploadContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
    padding: "5px",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    minHeight: "120px",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#1976d2",
    },
  },
  imagePreview: {
    width: "80px",
    height: "80px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  hiddenInput: {
    display: "none",
  },
});

const AddAndUpdateSalesInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const platformData = useSelector((state) => state?.platform);
  const supplierData = useSelector((state) => state?.vendor);
  const productData = useSelector((state) => state?.product);
  const variantData = useSelector((state) => state?.variant);
  const mobileView = useMediaQuery("(max-width:575px)");
  const [loading, setLoading] = useState(false);
  const [roundOff, setRoundOff] = useState(false);

  const validationSchema = yup.object({
    invoiceType: yup
      .string()
      .required("Please select a invoice type")
      .nullable(),
    vendorId: yup
      .string()
      .nullable()
      .when("invoiceType", {
        is: "Party",
        then: (schema) => schema.required("Please select a vendor"),
        otherwise: (schema) => schema.nullable(),
      }),
    platformId: yup
      .string()
      .nullable()
      .when("invoiceType", {
        is: "Platform",
        then: (schema) => schema.required("Please select a platform"),
        otherwise: (schema) => schema.nullable(),
      }),
    invoiceDate: yup.string().required("Please enter an invoice date."),
    gstPercentage: yup
      .number()
      .typeError("GST percentage must be a number")
      .min(0, "GST percentage must be 0 or greater")
      .max(100, "GST percentage cannot exceed 100%"),
    items: yup.array().of(
      yup.object().shape({
        productId: yup.string(),
        variantId: yup.string(),
        quantity: yup
          .number()
          .typeError("Quantity must be a number")
          .required("Please enter a quantity")
          .min(1, "Must be greater than or equal to 1"),
        rate: yup
          .number()
          .typeError("Rate must be a number")
          .required("Please enter a rate")
          .min(1, "Must be greater than or equal to 1"),
        amount: yup
          .number()
          .typeError("Amount must be a number")
          .required("Please enter an amount")
          .min(1, "Must be greater than or equal to 1"),
      })
    ),
  });

  const removeBlankStringKeys = (obj) =>
    Array.isArray(obj)
      ? obj.map(removeBlankStringKeys)
      : obj && typeof obj === "object"
      ? Object.fromEntries(
          Object.entries(obj)
            .filter(([_, v]) => v !== "")
            .map(([k, v]) => [k, removeBlankStringKeys(v)])
        )
      : obj;

  const formik = useFormik({
    initialValues: {
      invoiceType: "Platform",
      platformId: "",
      vendorId: "",
      invoiceDate: moment(new Date()).format("YYYY-MM-DD"),
      subTotal: 0,
      gstPercentage: "",
      gstAmount: 0,
      totalTaxAmount: 0,
      totalAmount: 0,
      items: [
        {
          variantId: "",
          productId: "",
          item: "",
          quantity: "",
          rate: "",
          amount: "",
        },
      ],
    },
    validationSchema: validationSchema,
    validateOnChange: false,
    validate: (values) => {
      const errors = {};

      values.items.forEach((item, index) => {
        if (!item.productId) {
          if (!errors.items) errors.items = [];
          if (!errors.items[index]) errors.items[index] = {};
          errors.items[index].productId = "Please select a product";
        }

        const productVariants =
          variantData?.list?.filter(
            (data) => data?.productId === item?.productId
          ) ?? [];

        if (productVariants.length > 0 && !item.variantId) {
          if (!errors.items) errors.items = [];
          if (!errors.items[index]) errors.items[index] = {};
          errors.items[index].variantId = "Please select a variant";
        }
      });

      return errors;
    },
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      let itemList = values?.items?.map((item) => {
        if (item?.item) {
          return { ...item, productId: "" };
        }
        return item;
      });

      const newValues = {
        ...values,
        items: itemList,
      };

      const newItems = removeBlankStringKeys(newValues?.items);
      const finalValues = { ...newValues, items: newItems };
      finalValues.roundOff = roundOff;

      if (finalValues?.invoiceType === "Platform") {
        delete finalValues?.vendorId;
      } else if (finalValues?.invoiceType === "Party") {
        delete finalValues?.platformId;
      }

      try {
        const response = await dispatch(
          id
            ? updateSalesInvoice({ id, data: finalValues })
            : createSalesInvoice(finalValues)
        ).unwrap();

        if (response?.status === 200) {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "success",
          });
          navigate(SALES_INVOICE);
          resetForm();
        } else {
          enqueueSnackbar(response.data?.responseMessage, {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(error?.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors, handleBlur } = formik;
  const { isSubmitting } = formik;

  const calculateTotalAmount = (items) => {
    return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  };

  // Calculate tax amounts based on percentages
  const calculateTaxAmounts = (
    subTotal,
    gstPercentage,
    shouldRoundOff = false
  ) => {
    const subTotalNum = Number(subTotal) || 0;
    const gstPercent = Number(gstPercentage) || 0;

    let gstAmount = 0;
    let totalTaxAmount = 0;

    gstAmount = (subTotalNum * gstPercent) / 100;
    totalTaxAmount = gstAmount;

    // Apply rounding if roundOff is enabled
    if (shouldRoundOff) {
      gstAmount = Math.round(gstAmount);
      totalTaxAmount = gstAmount;
    }

    return {
      gstAmount: gstAmount,
      totalTaxAmount: totalTaxAmount,
      totalAmount: shouldRoundOff
        ? Math.round(subTotalNum + totalTaxAmount)
        : subTotalNum + totalTaxAmount,
    };
  };

  const handleChange = (index, field, value, isCustomProduct) => {
    let updatedFields = [...values.items];
    updatedFields[index][field] = value;

    if (field === "productId") {
      updatedFields[index].variantId = "";
    }

    if (field === "quantity" || field === "rate") {
      const quantity = Number(updatedFields[index].quantity) || 0;
      const rate = Number(updatedFields[index].rate) || 0;
      updatedFields[index].amount = quantity * rate;
    }

    const totalAmount = calculateTotalAmount(updatedFields);

    setFieldValue("items", updatedFields);
    setFieldValue("subTotal", totalAmount);

    const taxCalculations = calculateTaxAmounts(
      totalAmount,
      values.gstPercentage,
      roundOff
    );

    setFieldValue("gstAmount", taxCalculations.gstAmount);
    setFieldValue("totalTaxAmount", taxCalculations.totalTaxAmount);
    setFieldValue("totalAmount", taxCalculations.totalAmount);

    if (field === "productId") {
      setFieldValue(`items[${index}].variantId`, "");
    }

    if (field === "productId" && isCustomProduct) {
      setFieldValue(`items[${index}].item`, value);
    } else if (field === "productId" && !isCustomProduct) {
      setFieldValue(`items[${index}].item`, "");
    }
  };
  // Handle tax percentage changes
  const handleTaxChange = (field, value) => {
    const numValue = Number(value);
    if (isNaN(numValue) || numValue < 0) {
      return;
    }

    setFieldValue(field, numValue);

    const taxCalculations = calculateTaxAmounts(
      values.subTotal,
      field === "gstPercentage" ? numValue : values?.gstPercentage
    );

    setFieldValue("gstAmount", taxCalculations?.gstAmount);
    setFieldValue("totalTaxAmount", taxCalculations?.totalTaxAmount);
    setFieldValue("totalAmount", taxCalculations?.totalAmount);
  };

  const handleAddMore = () => {
    const newItems = [
      ...formik.values.items,
      {
        variantId: "",
        productId: "",
        item: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ];
    formik.setValues({
      ...formik.values,
      items: newItems,
      subTotal: calculateTotalAmount(newItems),
    });
  };

  const handleDeleteField = (index) => {
    let updatedFields = [...values?.items];
    updatedFields.splice(index, 1);
    setFieldValue("items", updatedFields);
    setFieldValue("subTotal", calculateTotalAmount(updatedFields));
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(getByIdSalesInvoice(id))
        .unwrap()
        .then((response) => {
          if (response?.status === 200) {
            const invoiceDetail = response?.data?.data;

            setFieldValue(
              "invoiceDate",
              moment(invoiceDetail?.invoiceDate).format("YYYY-MM-DD")
            );
            setRoundOff(invoiceDetail?.roundOff || false);
            setFieldValue("subTotal", invoiceDetail?.subTotal);
            setFieldValue("platformId", invoiceDetail?.platformId?._id || "");
            setFieldValue("vendorId", invoiceDetail?.vendorId?._id || "");
            setFieldValue("invoiceType", invoiceDetail?.invoiceType);
            setFieldValue("gstPercentage", invoiceDetail?.gstPercentage);
            setFieldValue("gstAmount", invoiceDetail?.gstAmount);

            let productArr = [];
            for (let i = 0; i < invoiceDetail?.items.length; i++) {
              const element = invoiceDetail?.items[i];
              productArr?.push({
                variantId: element?.variantId?._id,
                productId: element?.productId?._id || element?.item,
                item: element?.item || "",
                quantity: element?.quantity,
                rate: element?.rate,
                amount: element?.amount,
              });
            }
            setFieldValue("items", productArr);

            setLoading(false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
            setLoading(false);
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    dispatch(getProductList({ sort: 1, field: "name" }));
    dispatch(getVendorList({ sort: 1, field: "name" }));
    dispatch(getVariantList());
    dispatch(getPlatformList({ sort: 1, field: "name" }));
  }, []);

  // Recalculate tax amounts when gstPercentage, subTotal or roundOff changes
  useEffect(() => {
    if (values?.subTotal > 0) {
      const taxCalculations = calculateTaxAmounts(
        values?.subTotal,
        values?.gstPercentage,
        roundOff
      );

      setFieldValue("gstAmount", taxCalculations.gstAmount);
      setFieldValue("totalTaxAmount", taxCalculations.totalTaxAmount);
      setFieldValue("totalAmount", taxCalculations.totalAmount);
    }
  }, [values?.gstPercentage, values?.subTotal, roundOff, setFieldValue]);

  const { handleSubmit } = formik;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <PageContainer heading={id ? "Update Invoice" : "Add Invoice"}>
          <Card>
            <CardContent>
              <FormikProvider value={formik}>
                <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
                  <Grid container border={1}>
                    <Grid sm={4} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1}>
                      <FormControl fullWidth>
                        <InputLabel>Invoice Type*</InputLabel>
                        <Select
                          name="isInvoiceCreated"
                          id="isInvoiceCreated"
                          value={values?.invoiceType ?? ""}
                          label="Invoice Type"
                          size="small"
                          onChange={(e) => {
                            setFieldValue("invoiceType", e.target.value);
                            setFieldValue("platformId", "");
                            setFieldValue("vendorId", "");
                          }}
                        >
                          <MenuItem value={"Platform"}>Platform</MenuItem>
                          <MenuItem value={"Party"}>Party</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid sm={4} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1}>
                      {values?.invoiceType === "Platform" ? (
                        <JumboAutoComplete
                          options={platformData?.list ?? []}
                          label="Platform*"
                          size="small"
                          getOptionLabel={(option) => option?.name}
                          name={"platformId"}
                          value={platformData?.list?.find(
                            (item) => item?._id === values?.platformId
                          )}
                          onChange={(evt, value) => {
                            setFieldValue("platformId", value?._id);
                          }}
                          helperText={formik?.errors?.platformId}
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                        />
                      ) : (
                        ""
                      )}
                      {values?.invoiceType === "Party" ? (
                        <JumboAutoComplete
                          options={supplierData?.list ?? []}
                          label="Vendor*"
                          size="small"
                          getOptionLabel={(option) => option?.name}
                          name={"vendorId"}
                          value={supplierData?.list?.find(
                            (item) => item?._id === values?.vendorId
                          )}
                          onChange={(evt, value) => {
                            setFieldValue("vendorId", value?._id);
                          }}
                          helperText={formik?.errors?.vendorId}
                          isOptionEqualToValue={(option, value) =>
                            option?._id === value?._id
                          }
                        />
                      ) : (
                        ""
                      )}
                    </Grid>
                    <Grid item sm={4} xs={12} p={1}>
                      <TextField
                        label={"Invoice Date*"}
                        name="invoiceDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("invoiceDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.invoiceDate}
                        error={errors?.invoiceDate}
                        helperText={errors?.invoiceDate}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    borderRight={1}
                    sx={{
                      mt: { xs: 3, md: 0 },
                    }}
                    className="mobile-view-table"
                  >
                    <TableContainer component={Paper}>
                      <Table
                        sx={{ minWidth: 650 }}
                        size="small"
                        aria-label="a dense table"
                        className={`${classes?.table}`}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell>Sr. No.</TableCell>
                            <TableCell style={{ minWidth: "200px" }}>
                              Product
                            </TableCell>
                            <TableCell style={{ minWidth: "200px" }}>
                              Variant
                            </TableCell>
                            <TableCell style={{ minWidth: "120px" }}>
                              Quantity
                            </TableCell>
                            <TableCell style={{ minWidth: "170px" }}>
                              Rate
                            </TableCell>
                            <TableCell
                              style={{ minWidth: "160px", textAlign: "end" }}
                            >
                              Amount
                            </TableCell>
                            <TableCell></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values?.items?.length &&
                            values?.items?.map((item, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell
                                    className={classes?.alignTop}
                                    sx={{ width: "10px", paddingTop: "20px" }}
                                  >
                                    {index + 1}
                                  </TableCell>

                                  <TableCell className={`${classes?.alignTop}`}>
                                    <Autocomplete
                                      id="productId"
                                      name="productId"
                                      size="small"
                                      freeSolo
                                      selectOnFocus
                                      clearOnBlur
                                      handleHomeEndKeys
                                      sx={{ marginTop: "7px" }}
                                      options={productData?.list ?? []}
                                      getOptionLabel={(option) => {
                                        if (typeof option === "string") {
                                          return option;
                                        }
                                        if (option.inputValue) {
                                          return option.inputValue;
                                        }
                                        return option?.name || "";
                                      }}
                                      filterOptions={(options, params) => {
                                        const filtered = filter(
                                          options,
                                          params
                                        );
                                        const { inputValue } = params;

                                        // Check if typed value already exists in options
                                        const isExisting = options.some(
                                          (option) =>
                                            option.name?.toLowerCase() ===
                                            inputValue.toLowerCase()
                                        );

                                        if (inputValue !== "" && !isExisting) {
                                          filtered.push({
                                            inputValue: inputValue,
                                            name: `Add "${inputValue}"`,
                                          });
                                        }

                                        return filtered;
                                      }}
                                      defaultValue={
                                        productData?.list?.find(
                                          (data) =>
                                            data?._id === item?.productId
                                        ) || item?.productId
                                      }
                                      openOnFocus
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          label="Product*"
                                          placeholder="Select or add a Product"
                                          error={
                                            formik?.errors?.items?.length &&
                                            formik?.errors?.items[index]
                                              ?.productId
                                          }
                                          helperText={
                                            formik?.errors?.items?.length &&
                                            formik?.errors?.items[index]
                                              ?.productId
                                          }
                                        />
                                      )}
                                      onBlur={handleBlur}
                                      onChange={(event, newValue) => {
                                        if (typeof newValue === "string") {
                                          // User typed text and pressed enter
                                          handleChange(
                                            index,
                                            "productId",
                                            newValue
                                          );
                                        } else if (
                                          newValue &&
                                          newValue.inputValue
                                        ) {
                                          // User selected "Add 'xyz'"
                                          handleChange(
                                            index,
                                            "productId",
                                            newValue.inputValue,
                                            true
                                          );
                                        } else {
                                          // Selected existing product
                                          handleChange(
                                            index,
                                            "productId",
                                            newValue?._id
                                          );
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <JumboAutoComplete
                                      sx={{ marginTop: "7px" }}
                                      options={
                                        variantData?.list?.filter(
                                          (data) =>
                                            data?.productId === item?.productId
                                        ) ?? []
                                      }
                                      label={
                                        variantData?.list?.filter(
                                          (data) =>
                                            data?.productId === item?.productId
                                        )?.length > 0
                                          ? "Variant*"
                                          : "Variant"
                                      }
                                      size="small"
                                      getOptionLabel={(option) => option?.name}
                                      name={`items[${index}].variantId`}
                                      value={
                                        item?.variantId
                                          ? variantData?.list?.find(
                                              (data) =>
                                                data?._id === item?.variantId
                                            )
                                          : null
                                      }
                                      onChange={(evt, value) => {
                                        handleChange(
                                          index,
                                          "variantId",
                                          value?._id
                                        );
                                      }}
                                      helperText={
                                        formik?.errors?.items?.length &&
                                        formik?.errors?.items[index]?.variantId
                                      }
                                      isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Qty"
                                      label="Qty*"
                                      name={`items[${index}].quantity`}
                                      fullWidth
                                      value={item?.quantity}
                                      size="small"
                                      type="number"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "quantity",
                                          +evt.target.value
                                        )
                                      }
                                      error={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                      helperText={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.quantity
                                      }
                                    />
                                  </TableCell>
                                  <TableCell className={`${classes?.alignTop}`}>
                                    <TextField
                                      sx={{ marginTop: "7px" }}
                                      placeholder="Rate"
                                      label="Rate*"
                                      name={`items[${index}].rate`}
                                      fullWidth
                                      value={item?.rate}
                                      size="small"
                                      type="number"
                                      onChange={(evt) =>
                                        handleChange(
                                          index,
                                          "rate",
                                          +evt.target.value
                                        )
                                      }
                                      error={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.rate
                                      }
                                      helperText={
                                        errors?.items?.length &&
                                        errors?.items?.[index]?.rate
                                      }
                                    />
                                  </TableCell>
                                  <TableCell
                                    sx={{ width: "10px", textAlign: "end" }}
                                  >
                                    {item?.amount || 0}
                                  </TableCell>
                                  <TableCell sx={{ width: "10px" }}>
                                    {values?.items?.length > 1 && (
                                      <IconButton
                                        aria-label="delete"
                                        color="secondary"
                                        onClick={() => handleDeleteField(index)}
                                      >
                                        <Cancel />
                                      </IconButton>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Grid className={classes?.buttonContainer}>
                                <Button
                                  style={{ minWidth: "100px" }}
                                  type="button"
                                  buttonLabel={"Add"}
                                  startIcon={<Add />}
                                  variant="outlined"
                                  onClick={handleAddMore}
                                  className="add-btn"
                                />
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                  <Grid container border={1} borderTop={0}>
                    <Grid sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1.2}>
                      <TextField
                        label={"GST(%)*"}
                        name="gstPercentage"
                        fullWidth
                        size="small"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        onChange={(evt) =>
                          handleTaxChange("gstPercentage", +evt.target.value)
                        }
                        onBlur={async (e) => {
                          handleBlur(e);
                        }}
                        InputLabelProps={{ shrink: true }}
                        value={values?.gstPercentage}
                        error={errors?.gstPercentage}
                        helperText={errors?.gstPercentage}
                      />
                    </Grid>
                    <Grid sm={3} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1.2}>
                      <TextField
                        disabled
                        label={"GST Amount"}
                        name="gstAmount"
                        fullWidth
                        size="small"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        value={values?.gstAmount}
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    </Grid>
                    <Grid sm={3} xs={12} borderRight={{ xs: 0, sm: 0 }} p={1.2}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "100%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={roundOff}
                              onChange={(e) => {
                                setRoundOff(e.target.checked);
                                // Recalculate amounts when roundoff changes
                                const taxCalculations = calculateTaxAmounts(
                                  values.subTotal || 0,
                                  values.gstPercentage || 0,
                                  e.target.checked
                                );

                                setFieldValue(
                                  "gstAmount",
                                  taxCalculations.gstAmount
                                );
                                setFieldValue(
                                  "totalTaxAmount",
                                  taxCalculations.totalTaxAmount
                                );
                                setFieldValue(
                                  "totalAmount",
                                  taxCalculations.totalAmount
                                );
                              }}
                              color="primary"
                            />
                          }
                          label="Round Off"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    border={1}
                    borderTop={0}
                    sx={{ fontWeight: 500 }}
                  >
                    <Grid sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1.2}>
                      Sub Total Amount
                    </Grid>
                    <Grid
                      sm={6}
                      xs={12}
                      textAlign={"right"}
                      borderRight={{ xs: 0, sm: 0 }}
                      p={1.2}
                    >
                      {values?.subTotal || 0}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    border={1}
                    borderTop={0}
                    sx={{ fontWeight: 500 }}
                  >
                    <Grid sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1.2}>
                      Total Tax Amount
                    </Grid>
                    <Grid
                      sm={6}
                      xs={12}
                      textAlign={"right"}
                      borderRight={{ xs: 0, sm: 0 }}
                      p={1.2}
                    >
                      {values?.totalTaxAmount || 0}
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    border={1}
                    borderTop={0}
                    sx={{ fontWeight: 500 }}
                  >
                    <Grid sm={6} xs={12} borderRight={{ xs: 0, sm: 1 }} p={1.2}>
                      Total Amount
                    </Grid>
                    <Grid
                      sm={6}
                      xs={12}
                      textAlign={"right"}
                      borderRight={{ xs: 0, sm: 0 }}
                      p={1.2}
                    >
                      {values?.totalAmount || 0}
                    </Grid>
                  </Grid>

                  <Grid container spacing={3}>
                    <Grid
                      item
                      sm={12}
                      xs={12}
                      className={classes?.loadingButtonContainer}
                    >
                      <LoadingButton
                        type="submit"
                        buttonLabel={id ? "Update" : "Save"}
                        sx={{ marginRight: "10px" }}
                        startIcon={<Save />}
                        variant="outlined"
                        fullWidth={mobileView}
                        loading={isSubmitting}
                      />
                      <LoadingButton
                        color={"secondary"}
                        buttonLabel={"Close"}
                        variant="outlined"
                        startIcon={<Close />}
                        onClick={() => navigate(SALES_INVOICE)}
                        fullWidth={mobileView}
                      />
                    </Grid>
                  </Grid>
                </Form>
              </FormikProvider>
            </CardContent>
          </Card>
        </PageContainer>
      )}
    </>
  );
};

export default AddAndUpdateSalesInvoice;
